import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard, CalendarPlus, CalendarDays, MessageCircle, Users, Settings,
  ShieldCheck, LogOut, Bell, Leaf, Menu, X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import ChatWidget from "@/components/ChatWidget";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAVS = {
  client: [
    { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, to: "/app" },
    { key: "book", label: "Book", icon: CalendarPlus, to: "/app/book" },
    { key: "bookings", label: "Sessions", icon: CalendarDays, to: "/app/bookings" },
    { key: "chat", label: "Chat", icon: MessageCircle, to: "/app/chat" },
  ],
  coach: [
    { key: "overview", label: "Overview", icon: LayoutDashboard, to: "/coach?tab=overview" },
    { key: "escalations", label: "Chats", icon: MessageCircle, to: "/coach?tab=escalations" },
    { key: "clients", label: "Clients", icon: Users, to: "/coach?tab=clients" },
    { key: "setup", label: "Setup", icon: Settings, to: "/coach?tab=setup" },
  ],
  admin: [
    { key: "overview", label: "Overview", icon: LayoutDashboard, to: "/admin?tab=overview" },
    { key: "users", label: "Users", icon: Users, to: "/admin?tab=users" },
    { key: "integrations", label: "Systems", icon: ShieldCheck, to: "/admin?tab=integrations" },
    { key: "audit", label: "Audit", icon: Bell, to: "/admin?tab=audit" },
  ],
};

export default function AppShell({ children, active }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifs, setNotifs] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);

  const role = user?.role || "client";
  const items = NAVS[role] || NAVS.client;

  useEffect(() => {
    api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  }, [location.pathname, location.search]);

  const unread = notifs.filter((n) => !n.read).length;

  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const isActive = (item) => {
    if (item.to.includes("?")) {
      const tab = new URLSearchParams(item.to.split("?")[1]).get("tab");
      const current = new URLSearchParams(location.search).get("tab") || items[0].key;
      return current === tab;
    }
    return active ? active === item.key : location.pathname === item.to;
  };

  return (
    <div className="min-h-screen pb-32">
      <header className="sticky top-0 z-40 glass px-5 sm:px-8 py-4 flex items-center justify-between" data-testid="app-header">
        <Link to={role === "client" ? "/app" : `/${role}`} className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-2xl zenith-cta flex items-center justify-center">
            <Leaf className="w-5 h-5 text-white" strokeWidth={1.5} />
          </span>
          <span className="text-xl font-headline font-medium text-primary tracking-tight">Vitalize</span>
        </Link>

        <div className="flex items-center gap-2 sm:gap-3">
          <DropdownMenu open={notifOpen} onOpenChange={setNotifOpen}>
            <DropdownMenuTrigger asChild>
              <button className="relative w-11 h-11 rounded-2xl flex items-center justify-center text-on-surface-variant hover:bg-surface-container transition-colors duration-300" data-testid="notif-button">
                <Bell className="w-5 h-5" strokeWidth={1.5} />
                {unread > 0 && (
                  <span className="absolute top-2 right-2 w-4 h-4 rounded-full bg-secondary text-white text-[10px] flex items-center justify-center">{unread}</span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 rounded-2xl border-0 shadow-ambient-lg bg-surface-container-lowest">
              <DropdownMenuLabel className="font-headline text-primary">Notifications</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {notifs.length === 0 && <div className="px-3 py-6 text-sm text-on-surface-variant text-center">You're all caught up.</div>}
              {notifs.slice(0, 8).map((n) => (
                <div key={n.notif_id} className="px-3 py-2.5 rounded-xl hover:bg-surface-container-low" data-testid="notif-item">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-primary-fixed text-primary-on-container">{n.channel}</span>
                    <p className="text-sm font-medium text-on-surface">{n.title}</p>
                  </div>
                  <p className="text-xs text-on-surface-variant mt-1">{n.body}</p>
                </div>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-11 h-11 rounded-full overflow-hidden bg-surface-container-highest flex items-center justify-center" data-testid="avatar-button">
                {user?.picture ? (
                  <img src={user.picture} alt="avatar" className="w-full h-full object-cover" />
                ) : (
                  <span className="font-headline text-primary text-sm">{(user?.firstName?.[0] || "U") + (user?.lastName?.[0] || "")}</span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 rounded-2xl border-0 shadow-ambient-lg bg-surface-container-lowest">
              <DropdownMenuLabel>
                <p className="font-medium text-on-surface">{user?.firstName} {user?.lastName}</p>
                <p className="text-xs text-on-surface-variant font-normal capitalize">{user?.role}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onLogout} className="rounded-xl cursor-pointer text-on-surface" data-testid="logout-button">
                <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 sm:px-8 pt-8 sm:pt-12 animate-rise-in">{children}</main>

      <nav className="fixed bottom-6 left-1/2 -translate-x-1/2 w-[92%] max-w-md glass rounded-full p-2 flex items-center justify-around z-40 shadow-ambient-lg" data-testid="bottom-nav">
        {items.map((item) => {
          const Icon = item.icon;
          const activeState = isActive(item);
          return (
            <button
              key={item.key}
              onClick={() => navigate(item.to)}
              className={`flex flex-col items-center px-3 py-2 rounded-full transition-all duration-300 min-w-[56px] ${activeState ? "text-primary" : "text-outline hover:text-primary"}`}
              data-testid={`nav-${item.key}`}
            >
              <span className={`transition-all duration-300 ${activeState ? "drop-shadow-[0_0_8px_rgba(85,98,77,0.4)]" : ""}`}>
                <Icon className="w-6 h-6" strokeWidth={activeState ? 2 : 1.5} />
              </span>
              <span className="text-[10px] mt-1 font-label">{item.label}</span>
            </button>
          );
        })}
      </nav>

      {role === "client" && <ChatWidget />}
    </div>
  );
}
